import React, { useRef, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, ACTIVITIES_PRIMARY, LANGUAGES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, MapPin, Loader2, Camera, Trash2 } from "lucide-react";
import Avatar from "@/components/Avatar";
import { resizeImageFile } from "@/lib/image";

export default function Profile() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [form, setForm] = useState({
    city: user.city || "",
    languages_spoken: user.languages_spoken || [],
    primary_activities: user.primary_activities || [],
    secondary_activities: (user.secondary_activities || []).join(", "),
    preferred_group_size: user.preferred_group_size || 4,
    latitude: user.latitude,
    longitude: user.longitude,
  });
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const toggle = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const useLocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success(t("locationCaptured"));
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", {
        ...form,
        secondary_activities: form.secondary_activities.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refresh();
      toast.success(t("saved"));
    } catch {
      toast.error(t("networkErr"));
    } finally { setSaving(false); }
  };

  const verify = async () => {
    try {
      await api.post("/auth/verify");
      await refresh();
      toast.success(t("verified"));
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="profile-page">
      <h1 className="text-3xl font-heading font-semibold tracking-tight">{t("yourProfile")}</h1>

      <div className="mt-6 bg-white border border-[hsl(var(--border))] rounded-3xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(31,30,27,0.04)]">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar user={user} size={72} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={avatarUploading}
              data-testid="avatar-upload-btn"
              aria-label="Change profile picture"
              className="absolute -bottom-1 -end-1 h-8 w-8 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center border-2 border-white shadow-md hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60"
            >
              {avatarUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="avatar-file-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
                setAvatarUploading(true);
                try {
                  const b64 = await resizeImageFile(file, 256, 0.85);
                  await api.put("/auth/profile", { avatar_b64: b64 });
                  await refresh();
                  toast.success(t("saved"));
                } catch {
                  toast.error(t("networkErr"));
                } finally { setAvatarUploading(false); }
              }}
            />
          </div>
          <div className="flex-1">
            <div className="text-xl font-heading font-semibold">{user.alias}</div>
            <div className="flex items-center gap-1.5 mt-0.5 text-sm">
              {user.verified ? (
                <span className="flex items-center gap-1 text-[hsl(var(--primary))]"><ShieldCheck className="h-4 w-4" /> {t("verified")}</span>
              ) : (
                <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]"><ShieldAlert className="h-4 w-4" /> {t("notVerified")}</span>
              )}
            </div>
            {user.avatar_b64 && (
              <button
                type="button"
                data-testid="avatar-remove-btn"
                onClick={async () => {
                  setAvatarUploading(true);
                  try {
                    await api.put("/auth/profile", { avatar_b64: "" });
                    await refresh();
                    toast.success(t("saved"));
                  } catch {} finally { setAvatarUploading(false); }
                }}
                className="mt-1 inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
              >
                <Trash2 className="h-3 w-3" /> {t("removePhoto")}
              </button>
            )}
          </div>
          {!user.verified && (
            <Button onClick={verify} variant="outline" data-testid="verify-btn" className="rounded-full border-[hsl(var(--border))]">
              {t("verifyMe")}
            </Button>
          )}
        </div>

        <div className="mt-8 space-y-6">
          <div>
            <Label>{t("city")}</Label>
            <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl h-11 flex-1" data-testid="profile-city-input" />
              <Button type="button" variant="outline" onClick={useLocation} disabled={geoLoading} data-testid="profile-geo-btn" className="rounded-xl h-11 gap-2 border-[hsl(var(--border))]">
                {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                {form.latitude ? t("locationCaptured") : t("useMyLocation")}
              </Button>
            </div>
          </div>

          <div>
            <Label>{t("languages")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {LANGUAGES.map((l) => {
                const active = form.languages_spoken.includes(l.name);
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => toggle("languages_spoken", l.name)}
                    data-testid={`profile-lang-${l.code}`}
                    className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${
                      active ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--secondary))]"
                    }`}
                  >
                    {l.native}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>{t("primaryActivities")}</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {ACTIVITIES_PRIMARY.map((a) => {
                const active = form.primary_activities.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggle("primary_activities", a)}
                    data-testid={`profile-act-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-3 py-2 rounded-2xl text-sm font-medium border transition-all ${
                      active ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
                    }`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>{t("secondaryActivities")}</Label>
            <Input value={form.secondary_activities} onChange={(e) => setForm({ ...form, secondary_activities: e.target.value })} placeholder={t("secondaryHint")} className="mt-1.5 rounded-xl h-11" data-testid="profile-secondary-input" />
          </div>

          <div>
            <Label>{t("preferredGroupSize")}: <span className="font-semibold text-[hsl(var(--primary))]">{form.preferred_group_size}</span></Label>
            <Slider className="mt-3" value={[form.preferred_group_size]} min={2} max={10} step={1} onValueChange={(v) => setForm({ ...form, preferred_group_size: v[0] })} />
          </div>

          <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white hover:-translate-y-0.5 transition-all">
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
            {saving ? t("saving") : t("save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
